import React from 'react';
import { FlexyForm } from '@europrocurement/flexy-form';

import { FieldValues, useForm } from 'react-hook-form';

import { OptionType } from '@europrocurement/flexy-components';
import {
    createUserFormStructure,
    companyOptions,
    employerCompanyOptions,
} from '../../../functions/createUserFormStructure';
import { type UserInfosProps } from '../UserInfos';

import { type User } from '../../../types/type';

type ClassicValues = {
    prenom: User['prenom'];
    nom: User['nom'];
    initiales: User['initiales'];
    user: User['user'];
    mail: User['mail'];
    ligneDirecte: User['ligneDirecte'];
    portable: User['portable'];
    poste: User['poste'];
    fonction: User['fonction'];
};

export type UserFormReturn = {
    pole: string | undefined;
    service: string | undefined;
    antenne: string | undefined;
    idGroupeGie: string | undefined;
    idSociete: number | undefined;
    idSocieteEmployeur: number | undefined;
} & ClassicValues;

export type BaseForm = {
    pole: OptionType | undefined;
    service: OptionType | undefined;
    idGroupeGie: OptionType | undefined;
    antenne: OptionType | undefined;
    idSociete: OptionType | undefined;
    idSocieteEmployeur: OptionType | undefined;
} & ClassicValues;

export type UserFormProps = {
    user: User;
    poles: UserInfosProps['poles'];
    services: UserInfosProps['services'];
    groupes: UserInfosProps['groupes'];
    antennes: UserInfosProps['antennes'];
    handlePatchInfos: (
        payload: UserFormReturn,
        dirtyFields?: {
            [key: string]: boolean;
        },
    ) => void;
};

export const UserForm: React.FunctionComponent<UserFormProps> = function ({
    user,
    services,
    poles,
    groupes,
    antennes,
    handlePatchInfos,
}) {
    const defaultValues: BaseForm = {
        prenom: user.prenom,
        nom: user.nom,
        initiales: user.initiales,
        user: user.user,
        mail: user.mail,
        ligneDirecte: user?.ligneDirecte ?? '',
        portable: user.portable,
        poste: user.poste || null,
        fonction: user.fonction,

        pole: poles.find((option) => option.value === user.pole?.id),
        service: services.find((option) => option.value === user.service?.id),
        idGroupeGie: groupes.find((option) => option.value === user.idGroupeGie?.id),
        antenne: antennes.find((option) => option.value === user.antenne?.idAntenne),
        idSociete: companyOptions.find((option) => option.value === user.idSociete),
        idSocieteEmployeur: employerCompanyOptions.find(
            (option) => option.value === user.idSocieteEmployeur,
        ),
    };

    const formContext = useForm({
        mode: 'onBlur',
        defaultValues: defaultValues as FieldValues,
    });

    /**
     * Function that catch the difference between the initial form data and the submitted form data
     * @param initial
     * @param current
     * @returns payload : patch object
     */
    const getModifiedValues = (current: BaseForm) => {
        let formatedMail = current.mail;

        if (current.mail === '') {
            formatedMail = null;
        }

        const payload: UserFormReturn = {
            ...current,
            idSociete: current.idSociete?.value as number | undefined,
            idSocieteEmployeur: current.idSocieteEmployeur?.value as number | undefined,
            service: `/utilisateur/services/${current.service?.value}` as string | undefined,
            pole: `/utilisateur/poles/${current.pole?.value}` as string | undefined,
            idGroupeGie: `/utilisateur/groupes/${current.idGroupeGie?.value}` as string | undefined,
            antenne: `/utilisateur/antennes/${current.antenne?.value}` as string | undefined,
            mail: formatedMail,
        };

        return payload;
    };

    const onUserInfoSubmit = (currentFormValues: BaseForm) => {
        const payload = getModifiedValues(currentFormValues);
        const {
            formState: { dirtyFields },
        } = formContext;
        handlePatchInfos(payload, dirtyFields);
    };

    return (
        <FlexyForm
            formObject={defaultValues}
            formContext={formContext}
            formStructure={createUserFormStructure(services, poles, groupes, antennes)}
            onSubmit={onUserInfoSubmit}
            gridProps={{ maxWidth: 'md', rowSpacing: 0, columnSpacing: 2 }}
        />
    );
};

export default UserForm;
